'use client';

import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  token,
  type Token,
} from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import { useNumberFormatter } from '@terra/ui-formatters';
import { Button } from '../../components/button';
import { BodyStrong, MetaDefault } from '../../components/typography';
import { SegmentedCountBarItemOverlay } from './SegmentedCountBarItemOverlay';

type ColorToken = Extract<Token, `colors.${string}`>;

export type SegmentedCountBarItem<TKey extends string = string> = {
  key: TKey;
  label: string;
  colorToken: ColorToken;
  count: number;
  tooltip?: string;
  tooltipContent?: ReactNode;
  hoverCardContent?: ReactNode;
  dataTestid?: string;
};

export type SegmentedCountBarProps<TKey extends string = string> = {
  items: ReadonlyArray<SegmentedCountBarItem<TKey>>;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  density?: 'compact' | 'default';
  legendLayout?: 'inline' | 'stacked' | 'compact';
  showPercentages?: boolean;
  selectedKeys?: ReadonlyArray<TKey>;
  onSelect?: (key: TKey) => void;
};

type CalculatedSegmentedCountBarItem<TKey extends string> =
  SegmentedCountBarItem<TKey> & {
    percentage: number;
    widthPercentage: number;
  };

export function SegmentedCountBar<TKey extends string>({
  items,
  size = 'sm',
  density = 'default',
  legendLayout = 'inline',
  showPercentages = true,
  selectedKeys,
  onSelect,
}: SegmentedCountBarProps<TKey>) {
  const numberFormatter = useNumberFormatter();
  const [hoveredKey, setHoveredKey] = useState<TKey>();
  const selectedKeySet = useMemo(
    () =>
      selectedKeys && selectedKeys.length > 0 ? new Set(selectedKeys) : null,
    [selectedKeys],
  );
  const hasSelection = selectedKeySet !== null && selectedKeySet.size > 0;
  const calculatedItems = useMemo<
    Array<CalculatedSegmentedCountBarItem<TKey>>
  >(() => {
    const totalCount = items.reduce((sum, item) => sum + item.count, 0);
    const equalWidthPercentage = items.length > 0 ? 100 / items.length : 0;
    const calculated = items.map((item) => ({
      ...item,
      percentage:
        totalCount > 0 ? Math.round((item.count / totalCount) * 100) : 0,
      widthPercentage:
        totalCount > 0 ? (item.count / totalCount) * 100 : equalWidthPercentage,
    }));

    if (totalCount === 0 || calculated.length === 0) {
      return calculated;
    }

    const { largestIndex, percentagesSum } = calculated.reduce(
      (result, item, index) => ({
        largestIndex:
          item.count > (calculated[result.largestIndex]?.count ?? 0)
            ? index
            : result.largestIndex,
        percentagesSum: result.percentagesSum + item.percentage,
      }),
      { largestIndex: 0, percentagesSum: 0 },
    );
    const largestItem = calculated[largestIndex];

    if (!largestItem) {
      return calculated;
    }

    return calculated.map((item, index) =>
      index === largestIndex
        ? {
            ...item,
            percentage: 100 - (percentagesSum - item.percentage),
          }
        : item,
    );
  }, [items]);

  return (
    <VStack gap={density === 'compact' ? 3 : 4} alignItems="stretch" w="full">
      <Box
        display="flex"
        alignItems="center"
        overflow="hidden"
        borderRadius="sm"
        w="full"
        bg="bg.neutral.subtle"
        style={{
          height:
            size === 'xl'
              ? '32px'
              : size === 'lg'
                ? '28px'
                : size === 'md'
                  ? '20px'
                  : '8px',
          gap: '2px',
        }}
      >
        {calculatedItems.map((item) => {
          const isSelected = selectedKeySet?.has(item.key) ?? false;
          const shouldDim =
            hoveredKey != null
              ? hoveredKey !== item.key
              : hasSelection && !isSelected;
          const backgroundColor = token(item.colorToken);

          return (
            <SegmentedCountBarItemOverlay
              key={item.key}
              tooltip={item.tooltip}
              tooltipContent={item.tooltipContent}
              hoverCardContent={item.hoverCardContent}
            >
              <Button
                type="button"
                variant="ghost"
                aria-label={item.label}
                h="full"
                minW={item.count > 0 ? '1px' : 0}
                p={0}
                borderRadius={size === 'sm' ? 0 : 'sm'}
                flex="none"
                cursor={onSelect ? 'pointer' : 'default'}
                style={{
                  backgroundColor,
                  opacity: shouldDim ? 0.2 : 1,
                  width: `${item.widthPercentage}%`,
                }}
                transition="opacity 0.2s ease"
                _hover={{ opacity: 1, backgroundColor }}
                onMouseEnter={() => setHoveredKey(item.key)}
                onMouseLeave={() => setHoveredKey(undefined)}
                onClick={() => onSelect?.(item.key)}
              />
            </SegmentedCountBarItemOverlay>
          );
        })}
      </Box>

      <HStack
        gap={legendLayout === 'stacked' ? 2.5 : 4}
        alignItems="center"
        w="full"
        flexWrap="wrap"
        flexDirection={legendLayout === 'stacked' ? 'column' : 'row'}
      >
        {calculatedItems.map((item) => {
          const isActive = selectedKeySet?.has(item.key) ?? false;
          const backgroundColor = token(item.colorToken);

          return (
            <SegmentedCountBarItemOverlay
              key={item.key}
              tooltip={item.tooltip}
              tooltipContent={item.tooltipContent}
              hoverCardContent={item.hoverCardContent}
            >
              <Button
                type="button"
                variant="ghost"
                h={
                  legendLayout === 'stacked'
                    ? 5
                    : legendLayout === 'compact'
                      ? 4
                      : 'auto'
                }
                py={legendLayout === 'inline' ? 0.5 : 0}
                pl={legendLayout === 'compact' ? 0 : 1}
                pr={legendLayout === 'compact' ? 0 : 2}
                borderRadius="md"
                flexDirection={legendLayout === 'inline' ? 'column' : 'row'}
                gap={legendLayout === 'inline' ? 0 : 2}
                alignItems={legendLayout === 'inline' ? 'flex-start' : 'center'}
                justifyContent="center"
                w={legendLayout === 'stacked' ? 'full' : undefined}
                cursor={onSelect ? 'pointer' : 'default'}
                bg={isActive ? 'bg.neutral.hover' : 'transparent'}
                _hover={{ bg: 'bg.neutral.hover' }}
                onMouseEnter={() => setHoveredKey(item.key)}
                onMouseLeave={() => setHoveredKey(undefined)}
                onClick={() => onSelect?.(item.key)}
                {...automationId(item.dataTestid ?? '')}
              >
                <HStack gap={1} alignItems="center">
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    style={{ backgroundColor }}
                  />
                  <MetaDefault color="fg.muted">{item.label}</MetaDefault>
                </HStack>
                <HStack
                  gap={2}
                  alignItems="center"
                  ml={legendLayout === 'stacked' ? 'auto' : undefined}
                >
                  <BodyStrong as="span" color="fg.default">
                    {numberFormatter.format(item.count)}
                  </BodyStrong>
                  {showPercentages ? (
                    <MetaDefault color="fg.muted">
                      {item.percentage}%
                    </MetaDefault>
                  ) : null}
                </HStack>
              </Button>
            </SegmentedCountBarItemOverlay>
          );
        })}
      </HStack>
    </VStack>
  );
}
